import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import qs from 'qs';
import clsx from 'clsx';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';

import Loading from '../../common_components/Loading';
import {
  CorelationTabContent,
  LineRegressionTabContent,
  QqplotTabContent,
  NormStatisticsTabContent,
} from './TabContents';
import { DataPreviewer } from './controls/DataPreviewer';
import { Select } from './inputs/Select';
import { TextInput } from './inputs/Text';
import { MultiSelect } from './inputs/MultiSelect';
import { EquationsField } from './inputs/EquationsField';
import { Modal, LoadingModalBody, WarningModalBody } from './Modal';
import { UNKNOWN_PROCESSING_ERROR, WRONG_FORMAT } from '../constants';
import { getFileNameFromPath } from '../../utils';
import { isCorrectFileFormat } from '../utils';
import { Warning } from '../../icons';

const statisticalMethods = [
  {
    label: 'Testy Normalności',
    value: 'norm_statistic',
  },
  {
    label: 'Qqplot',
    value: 'qqplot',
  },
  {
    label: 'Korelacja',
    value: 'correlation',
  },
  {
    label: 'Regresja Liniowa',
    value: 'line_regression',
  },
];
const componentMapping = {
  qqplot: QqplotTabContent,
  correlation: CorelationTabContent,
  norm_statistic: NormStatisticsTabContent,
};
const initialMainPanelData = {
  norm_statistic: null,
  correlation: null,
  qqplot: null,
  line_regression: {
    plot: null,
    metrics: null,
    assumptionTests: null,
    evaluation: null,
  },
};

export const AnalysisPanel = ({
  projectData,
  setProjectData,
  setShowingWelcomeScreen,
}) => {
  const [analysisMethod, setAnalysisMethod] = useState('norm_statistic');
  const [visualizationTab, setVisualizationTab] = useState('plot');
  const [mainPanelData, setMainPanelData] = useState(initialMainPanelData);
  const [isCalculating, setIsCalculating] = useState(false);
  const [fileLoadError, setFileLoadError] = useState(null);
  const [calculationsError, setCalculationsError] = useState(null);
  const [openedModal, setOpenedModal] = useState(null);

  const initialRender = useRef(true);
  const modalRef = useRef(null);
  const fileFormRef = useRef(null);
  const handleFileChange = async (event) => {
    setFileLoadError(null);
    // Because later we need to hide 'loading' information
    // and show error information at the same time (when
    // loading finishes) we want to only change modal's
    // body - otherwise, if we were dealing with 2 modals
    // we would need to quickly close one and open another
    // which is a poor experience for the user.
    modalRef?.current?.showModal();
    setOpenedModal('loading');
    if (event.target.files?.[0]) {
      const formData = new FormData(fileFormRef.current);
      const file = formData.get('datafile');
      if (file && !isCorrectFileFormat(file)) {
        setFileLoadError(WRONG_FORMAT);
        setOpenedModal('warning');
        event.target.value = '';
        return;
      }
      await axios
        .post(window.DjUrls['ate:update_file'](), formData)
        .then((res) => {
          const projectData = res.data;
          setProjectData(projectData);
          const newSelectedSideFields = getDefaultSelectedFields(projectData);
          setSelectedSideFields(newSelectedSideFields);
          const newFilterParams = {
            column: projectData.filtering_field.default_value,
            value: getPossibleDependentOptions(
              projectData.filtering_field.default_value,
              projectData.filtering_field.dependent_field.values,
            )[0].value,
          };
          setFilterParams(newFilterParams);
          // whole data save in file needs resetting if any of the input parameters
          // changes (File, filter param1, filter param2)
          setMainPanelData(initialMainPanelData);
          if (isSidePanelDataSet(analysisMethod, newSelectedSideFields))
            fetchCalculationsData(
              visualizationTab,
              analysisMethod,
              newFilterParams,
              newSelectedSideFields[analysisMethod],
            );
          modalRef?.current?.close();
          // don't setOpenedModal(null) when closing to prevent 'no-body' glitch
        })
        .catch((error) => {
          const data = error?.response?.data;
          const code = data?.details?.code;
          setFileLoadError(code);
          setOpenedModal('warning');
          event.target.value = '';
        });
    } else {
      modalRef?.current?.close();
    }
  };

  const sideFieldsRenderingData = getStepDataFor(
    projectData?.steps,
    analysisMethod,
  );
  const [selectedSideFields, setSelectedSideFields] = useState(() =>
    getDefaultSelectedFields(projectData),
  );
  const isSidePanelDataSet = (analysisMethod, selectedSideFields) => {
    return !(
      analysisMethod == 'line_regression' &&
      (selectedSideFields.line_regression === null ||
        selectedSideFields.line_regression?.length === 0 ||
        !(selectedSideFields.line_regression?.equations?.length > 0))
    );
  };

  // filtering params
  const filterFieldRenderingData = projectData.filtering_field;
  const [filterParams, setFilterParams] = useState({
    column: filterFieldRenderingData.default_value,
    value: getPossibleDependentOptions(
      filterFieldRenderingData.default_value,
      filterFieldRenderingData.dependent_field.values,
    )[0].value,
  });

  const NonLrComponent = componentMapping[analysisMethod];

  const fetchCalculationsData = (
    visualizationTab,
    analysisMethod,
    filterParams,
    adjustParams,
  ) => {
    setIsCalculating(true);
    setCalculationsError(null);

    const formSubmitUrl = getFormSubmitUrl(analysisMethod, visualizationTab);
    const responseType = getResponseType(analysisMethod, visualizationTab);
    const params = adjustRequestParams(analysisMethod, {
      ...filterParams,
      ...adjustParams,
    });

    axios
      .get(formSubmitUrl, {
        responseType,
        params,
        paramsSerializer: (params) =>
          qs.stringify(
            params,
            // convert array params to Django's default format
            { arrayFormat: 'repeat' },
          ),
      })
      .then((res) => {
        setMainPanelData((p) =>
          analysisMethod === 'line_regression'
            ? {
                ...p,
                [analysisMethod]: {
                  ...p[analysisMethod],
                  // in line_regression we need to account for visualizationTab
                  [visualizationTab]: res.data,
                },
              }
            : { ...p, [analysisMethod]: res.data },
        );
        setIsCalculating(false);
      })
      .catch((error) => {
        const data = error.response?.data;
        const code = data?.details?.code;
        setMainPanelData((p) =>
          analysisMethod === 'line_regression'
            ? {
                ...p,
                [analysisMethod]: {
                  ...p[analysisMethod],
                  // in line_regression we need to account for visualizationTab
                  [visualizationTab]:
                    initialMainPanelData?.[analysisMethod]?.[visualizationTab],
                },
              }
            : {
                ...p,
                [analysisMethod]: initialMainPanelData?.[analysisMethod],
              },
        );
        setCalculationsError(code ?? UNKNOWN_PROCESSING_ERROR);
        setOpenedModal('warning');
        modalRef?.current?.showModal();
        setIsCalculating(false);
      });
  };
  useEffect(() => {
    fetchCalculationsData(
      visualizationTab,
      analysisMethod,
      filterParams,
      selectedSideFields[analysisMethod],
    );
  }, []);

  useEffect(() => {
    // debounce requests on text input changes
    const handler = setTimeout(() => {
      if (initialRender.current === false) {
        fetchCalculationsData(
          visualizationTab,
          analysisMethod,
          filterParams,
          selectedSideFields[analysisMethod],
        );
      } else {
        initialRender.current = false;
      }
    }, 1000);

    return () => clearTimeout(handler);
  }, [
    selectedSideFields.qqplot?.title,
    selectedSideFields.qqplot?.x_label,
    selectedSideFields.qqplot?.y_label,
  ]);

  return (
    <div className="[--app-radius:0.5rem] [--border-color:oklch(var(--b3))] w-full [&_.app-side-content]:bg-base-300 [&_.app-side-content_*]:text-black shadow-lg">
      {/* top panel */}
      <div className="flex bg-base-300 max-w-[--page-content-w] mx-auto rounded-t-[--app-radius] p-4 pb-6 pr-12 items-center shadow-sm">
        <Tippy
          content="przejdź do ekranu powitalnego"
          placement="right"
          theme="custom-dark"
        >
          <span
            className="text-6xl font-extrabold cursor-pointer"
            onClick={() => setShowingWelcomeScreen(true)}
          >
            ATE
          </span>
        </Tippy>
        <div className="relative border-2 border-dashed border-white rounded-lg items-center flex px-5 py-6 ml-auto mr-6 gap-5">
          <span className="absolute font-bold bg-base-300 -top-3 left-5 px-2 border-r">
            Dane do analizy:{' '}
          </span>
          <form className="flex gap-4 items-center" ref={fileFormRef}>
            Plik:
            <span
              className="truncate w-[200px] input input-sm"
              title={getFileNameFromPath(projectData.file)}
            >
              {getFileNameFromPath(projectData.file)}
            </span>
            <label
              htmlFor="file-input"
              className="btn btn-sm btn-primary"
              title="Zmień plik"
            >
              <input
                id="file-input"
                type="file"
                className="sr-only"
                name="datafile"
                onChange={handleFileChange}
              />
              Zmień
            </label>
          </form>
          <form className="flex gap-5">
            <div className="flex items-center">
              <span
                className="mr-3 text-sm"
                id={`${filterFieldRenderingData.form_name}-label`}
              >
                {filterFieldRenderingData.label}:
              </span>
              <select
                name={filterFieldRenderingData.form_name}
                value={filterParams.column}
                onChange={(event) => {
                  const newFilterParams = {
                    column: event.target.value,
                    value: getPossibleDependentOptions(
                      event.target.value,
                      filterFieldRenderingData.dependent_field.values,
                    )[0].value,
                  };
                  setFilterParams(newFilterParams);
                  // whole data save in file needs resetting if any of the input parameters
                  // changes (File, filter param1, filter param2)
                  setMainPanelData(initialMainPanelData);
                  if (isSidePanelDataSet(analysisMethod, selectedSideFields))
                    fetchCalculationsData(
                      visualizationTab,
                      analysisMethod,
                      newFilterParams,
                      selectedSideFields[analysisMethod],
                    );
                }}
                type="select"
                className="select select-sm w-[137px]"
                aria-labelledby={`${filterFieldRenderingData.form_name}-label`}
              >
                {filterFieldRenderingData.values.map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <span
                className="mr-3 text-sm"
                id={`${filterFieldRenderingData.dependent_field.form_name}-label`}
              >
                {filterFieldRenderingData.dependent_field.label}:
              </span>
              <select
                name={filterFieldRenderingData.dependent_field.form_name}
                aria-labelledby={`${filterFieldRenderingData.dependent_field.form_name}-label`}
                value={filterParams.value}
                onChange={(event) => {
                  const newFilterParams = {
                    ...filterParams,
                    value: event.target.value,
                  };
                  setFilterParams(newFilterParams);
                  // whole data save in file needs resetting if any of the input parameters
                  // changes (File, filter param1, filter param2)
                  setMainPanelData(initialMainPanelData);
                  if (isSidePanelDataSet(analysisMethod, selectedSideFields))
                    fetchCalculationsData(
                      visualizationTab,
                      analysisMethod,
                      newFilterParams,
                      selectedSideFields[analysisMethod],
                    );
                }}
                disabled={filterParams.column === ''}
                type="select"
                className={clsx('select select-sm w-24')}
              >
                {getPossibleDependentOptions(
                  filterParams.column,
                  filterFieldRenderingData.dependent_field.values,
                ).map(({ value, label }) => (
                  <option value={value} key={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </form>
          <DataPreviewer filterParams={filterParams} file={projectData?.file} />
        </div>
      </div>
      <div className="[&_img]:mt-0 w-full max-w-[--page-content-w] mx-auto border-black rounded-[--app-radius] mb-10">
        <div className="bg-base-300 flex px-6 gap-6 prose max-w-none border-t border-t-white">
          {/* side panel */}
          <div className="flex flex-col mb-6">
            <h2
              className="mt-12 mb-6"
              title="Metoda statystyczna stosowana do analizy"
              id="statistical-method-label"
            >
              Metoda statystyczna
            </h2>
            <Select
              defaultValue={analysisMethod}
              onChange={(newAnalysisMethod) => {
                setAnalysisMethod(newAnalysisMethod);
                if (!mainPanelData[newAnalysisMethod]) {
                  fetchCalculationsData(
                    visualizationTab,
                    newAnalysisMethod,
                    filterParams,
                    selectedSideFields[newAnalysisMethod],
                  );
                } else {
                  // if we've already fetched correct data for this method
                  // and then later changed to a method for which CalculationsError
                  // was set, when going back to the 'calculable' method,
                  // we need to set the error back to null in order to show the data.
                  // Otherwise the normal resetting that would be done when
                  // fetching calculations data is not done because we are
                  // relying on cached value.
                  // TODO: perhaps calculationsError could be an object holding
                  //  error status per method, thanks to this we probably
                  //  wouldn't need this reset here and the elaborate explanation
                  //  comment above.
                  setCalculationsError(null);
                }
              }}
              values={statisticalMethods}
              ariaLabelledBy="statistical-method-label"
            />
            <form className="flex flex-col max-w-72">
              <h2 className="mt-10 mb-0">Dostosuj</h2>
              {sideFieldsRenderingData?.map((field) => (
                <div
                  key={field.form_name}
                  className="flex flex-col items-stretch"
                >
                  <h3 id={`${field.form_name}-label`}>{field.label}</h3>
                  {field.type == 'select' && (
                    <Select
                      formName={field.form_name}
                      defaultValue={
                        selectedSideFields?.[analysisMethod]?.[
                          field.form_name
                        ] ?? field.default_value
                      }
                      values={field.values}
                      ariaLabelledBy={`${field.form_name}-label`}
                      onChange={(value) => {
                        const newSideFields = {
                          ...selectedSideFields,
                          [analysisMethod]: {
                            ...selectedSideFields[analysisMethod],
                            [field.form_name]: value,
                          },
                        };
                        setSelectedSideFields(newSideFields);
                        fetchCalculationsData(
                          visualizationTab,
                          analysisMethod,
                          filterParams,
                          newSideFields[analysisMethod],
                        );
                      }}
                    />
                  )}
                  {field.type == 'multiselect' && (
                    <MultiSelect
                      formName={field.form_name}
                      values={field.values}
                      currentlySelected={
                        selectedSideFields?.[analysisMethod]?.[
                          field.form_name
                        ] ?? field.default_value
                      }
                      maxAllowed={field.max_allowed}
                      minAllowed={field.min_allowed}
                      ariaLabelledBy={`${field.form_name}-label`}
                      ariaLabel={field.label}
                      onChange={(value) => {
                        const newSelectedSideFields = {
                          ...selectedSideFields,
                          [analysisMethod]: {
                            ...selectedSideFields[analysisMethod],
                            [field.form_name]: value,
                          },
                        };
                        setSelectedSideFields(newSelectedSideFields);
                        fetchCalculationsData(
                          visualizationTab,
                          analysisMethod,
                          filterParams,
                          newSelectedSideFields[analysisMethod],
                        );
                      }}
                    />
                  )}
                  {field.type == 'text' && (
                    <TextInput
                      formName={field.form_name}
                      defaultValue={
                        selectedSideFields?.[analysisMethod]?.[
                          field.form_name
                        ] ?? field.default_value
                      }
                      onChange={(value) => {
                        setSelectedSideFields((p) => ({
                          ...p,
                          [analysisMethod]: {
                            ...p[analysisMethod],
                            [field.form_name]: value,
                          },
                        }));
                        // text inputs are debounced so we don't fetch data here
                      }}
                    />
                  )}
                  {field.type == 'formula' && (
                    <EquationsField
                      values={field.values}
                      selectedValues={
                        selectedSideFields?.[analysisMethod]?.[field.form_name]
                      }
                      onChange={(value) => {
                        const newSelectedSideFields = {
                          ...selectedSideFields,
                          [analysisMethod]: {
                            [field.form_name]: value,
                          },
                        };
                        setSelectedSideFields(newSelectedSideFields);
                        setMainPanelData((p) => ({
                          ...p,
                          // need to reset mainPanelData for all LR
                          // tabs - if equationsField changes the data
                          // for all tabs becomes stale
                          line_regression: {
                            ...initialMainPanelData.line_regression,
                          },
                        }));
                        fetchCalculationsData(
                          visualizationTab,
                          analysisMethod,
                          filterParams,
                          newSelectedSideFields[analysisMethod],
                        );
                      }}
                    />
                  )}
                </div>
              ))}
            </form>
          </div>
          {/* main panel */}
          <div className="border-l border-l-white pl-6 w-full pt-6 pb-6 relative">
            {/* currently calculationsError can only be in 'null' / UNKNOWN_PROCESSING_ERROR state */}
            {calculationsError === UNKNOWN_PROCESSING_ERROR ? (
              <div className="flex flex-col justify-center">
                <h2 className="flex text-warning items-center text-center self-center mt-4">
                  <Warning className="pr-4 h-14 w-14" />
                  Nie udało się wykonać analizy dla wybranej metody
                  statystycznej
                </h2>
                <p className="text-lg mt-5">
                  Możesz spróbować zmienić metodę, parametry filtrowania,
                  parametry dostosowania (panel boczny) lub wgrać nowy zestaw
                  danych. Prosimy o zwracanie uwagi na instrukcje w komunikatach
                  błędów które zostaną wyświetlone na ekranie w przypadku
                  niepowodzenia.
                </p>
                <p className="text-lg">
                  Jeśli problem będzie się powtarzał możesz zwrócić się do nas
                  po pomoc na:
                  <a
                    className="ml-1 font-semibold whitespace-nowrap"
                    href="mailto:ote-portal@agh.edu.pl"
                  >
                    ote-portal@agh.edu.pl
                  </a>
                </p>
              </div>
            ) : (
              <div
                className={clsx(
                  'transition-opacity duration-300',
                  isCalculating && 'opacity-40',
                )}
              >
                {analysisMethod !== 'line_regression' ? (
                  isCalculating && !mainPanelData[analysisMethod] ? (
                    <div className="flex items-center justify-center w-full h-[600px]">
                      <Loading />
                    </div>
                  ) : isCalculating && mainPanelData[analysisMethod] ? (
                    <>
                      <Loading
                        msg="Aktualizowanie wyników"
                        className="absolute inset-0 bg-base-300/60 backdrop-blur-sm z-10 my-0"
                      />
                      <NonLrComponent data={mainPanelData[analysisMethod]} />
                    </>
                  ) : mainPanelData[analysisMethod] ? (
                    <NonLrComponent data={mainPanelData[analysisMethod]} />
                  ) : (
                    // fallback for initial render when we're not yet loading,
                    // but still don't have data
                    <></>
                  )
                ) : selectedSideFields.line_regression === null ||
                  selectedSideFields.line_regression?.length === 0 ||
                  (!mainPanelData.line_regression[visualizationTab] &&
                    !isCalculating) ? (
                  <div className="w-full h-full flex justify-center items-center">
                    <h2 className="text-info text-center">
                      Wprowadź formułę regresji liniowej prostej w panelu
                      bocznym. Wyniki wyświetlą się automatycznie.
                    </h2>
                  </div>
                ) : (
                  <>
                    <LineRegressionTabContent
                      data={mainPanelData.line_regression}
                      isLoading={isCalculating}
                      visualizationTab={visualizationTab}
                      onTabChange={(newVisualizationTab) => {
                        setVisualizationTab(newVisualizationTab);
                        if (
                          !mainPanelData['line_regression'][newVisualizationTab]
                        ) {
                          fetchCalculationsData(
                            newVisualizationTab,
                            'line_regression',
                            filterParams,
                            selectedSideFields['line_regression'],
                          );
                        }
                      }}
                    />
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Modal ref={modalRef}>
        {openedModal === 'loading' ? (
          <LoadingModalBody />
        ) : openedModal === 'warning' ? (
          <WarningModalBody errorCode={fileLoadError ?? calculationsError} />
        ) : (
          <></>
        )}
      </Modal>
    </div>
  );
};

function getDefaultSelectedFields(projectData) {
  return {
    norm_statistic: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'norm_statistic').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
    qqplot: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'qqplot').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
    correlation: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'correlation').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
    line_regression: Object.fromEntries(
      getStepDataFor(projectData?.steps, 'line_regression').map(
        ({ form_name, default_value }) => [form_name, default_value],
      ),
    ),
  };
}

function adjustRequestParams(analysisMethod, params) {
  if (analysisMethod === 'line_regression') {
    params = {
      ...params,
      // equations are a more complicated structure and need to be sent as JSON
      equations: JSON.stringify(params.equations),
    };
  } else if (analysisMethod === 'correlation') {
    params = {
      ...params,
      // extract value from {value, label} pairs
      numerical_columns: params.numerical_columns.map(({ value }) => value),
    };
  }
  return params;
}

function getPossibleDependentOptions(
  selectedParent,
  allDependentValuesOptions,
) {
  return allDependentValuesOptions.find(
    (v) => v.dependent_on === selectedParent,
  )?.values;
}

function getStepDataFor(stepData, analysisMethod) {
  return stepData?.find((step) => step.step_name === analysisMethod)?.step_data;
}

function getFormSubmitUrl(analysisMethod, visualizationTab) {
  if (analysisMethod !== 'line_regression') {
    return {
      qqplot: window.DjUrls['ate:qqplot'](),
      correlation: window.DjUrls['ate:correlation'](),
      norm_statistic: window.DjUrls['ate:norm_statistic'](),
    }[analysisMethod];
  }
  return {
    plot: window.DjUrls['ate:line_regresion_plot'](),
    metrics: window.DjUrls['ate:line_regresion_metrics'](),
    assumptionTests: window.DjUrls['ate:line_regresion_assumptions_tests'](),
    evaluation: window.DjUrls['ate:line_regresion_evaluation'](),
  }[visualizationTab];
}

function getResponseType(analysisMethod, visualizationTab) {
  return analysisMethod === 'line_regression'
    ? visualizationTab === 'plot'
      ? 'blob'
      : 'json'
    : analysisMethod === 'norm_statistic'
      ? 'json'
      : 'blob';
}
